import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, BarChart3, Bug, CheckCircle2, Eye, Flame, Info, Link2, ShieldCheck, Swords, Target, TrendingUp, Users } from 'lucide-react';
import { Badge, EmptyState, SectionCard } from '@/components/primitives';
import { Delta, ProgressBar, RankedBars, SEV_ORDER, SplitBar, sevVar } from '@/components/insights/visuals';
import DailyBars from '@/components/charts/DailyBars';
import { plural } from './format';
import '@/styles/analytics.css';

const TONE = {
  risk: { label: 'Risk', icon: AlertTriangle },
  watch: { label: 'Watch', icon: Eye },
  info: { label: 'Context', icon: Info },
  good: { label: 'Good news', icon: CheckCircle2 },
};

const TYPE_PARTS = [
  ['attack', 'Attacks', 'var(--chart-1)'],
  ['advisory', 'Advisories', 'var(--chart-3)'],
  ['informational', 'Informational', 'var(--text-muted)'],
];

function Insights({ items, total }) {
  if (!items.length) {
    return (
      <EmptyState
        variant="compact"
        icon={BarChart3}
        title={total < 5 ? 'Not enough data to draw conclusions yet' : 'Nothing stands out in this period'}
        description={total < 5 ? 'Insights appear once a handful of threats have matched your profile.' : 'No unusual change, concentration or backlog was detected.'}
      />
    );
  }
  return (
    <ul className="an-insights">
      {items.map((i) => {
        const tone = TONE[i.tone] || TONE.info;
        const Icon = tone.icon;
        return (
          <li key={i.id} className="an-insight" data-tone={i.tone}>
            <span className="an-insight-tag"><Icon aria-hidden="true" /> {tone.label}</span>
            <h3 className="an-insight-title">{i.title}</h3>
            <p className="an-insight-detail">{i.detail}</p>
          </li>
        );
      })}
    </ul>
  );
}

function PeriodStrip({ totals, window: w }) {
  const cells = [
    { key: 'total', label: 'All threats', value: totals.current, previous: totals.previous },
    ...SEV_ORDER.map((s) => ({ key: s, label: s, value: totals.by_severity[s].current, previous: totals.by_severity[s].previous, color: sevVar(s) })),
  ];
  return (
    <>
      <div className="an-strip">
        {cells.map((c) => (
          <div key={c.key} className="an-strip-cell">
            <span className="is-stat-label">{c.color && <i style={{ background: c.color }} aria-hidden="true" />}{c.label}</span>
            <span className="an-strip-value">{c.value}</span>
            {w.comparable && <Delta current={c.value} previous={c.previous} suffix="" />}
          </div>
        ))}
      </div>
      {!w.comparable && w.days && (
        <p className="an-note">
          Comparison with the previous {w.days} days needs more history. Your feed began {w.history_days === 0 ? 'today' : `${plural(w.history_days ?? 0, 'day')} ago`}, so changes are not shown rather than shown wrongly.
        </p>
      )}
    </>
  );
}

function Response({ response }) {
  const rows = SEV_ORDER.map((s) => [s, response.by_severity[s]]).filter(([, v]) => v.total > 0);
  return (
    <>
      <div className="an-response-head">
        <div>
          <span className="an-big">{response.handled_pct == null ? '-' : `${response.handled_pct}%`}</span>
          <span className="t-small"> of threats handled</span>
        </div>
        <span className="t-small">
          {response.median_hours_to_action == null
            ? 'Median time to act appears after three threats are handled'
            : `Median time to act: ${response.median_hours_to_action < 48 ? `${response.median_hours_to_action} hours` : `${Math.round(response.median_hours_to_action / 24)} days`}`}
        </span>
      </div>
      <ul className="an-handled">
        {rows.map(([s, v]) => (
          <li key={s}>
            <span className="an-handled-label"><i style={{ background: sevVar(s) }} aria-hidden="true" />{s}</span>
            <ProgressBar value={v.handled} total={v.total} tone={s.toLowerCase()} label={`${s} threats handled`} />
            <span className="an-handled-n">{v.handled}/{v.total}</span>
          </li>
        ))}
      </ul>
      <p className="t-small an-foot">
        Handled means acknowledged, shipped or archived. Opening a threat only moves it to triage.
        {response.backlog_critical_high > 0 && <> <strong>{response.backlog_critical_high}</strong> Critical or High threats are still open.</>}
      </p>
    </>
  );
}

function KillChain({ tactics }) {
  if (!tactics.length) return <p className="ins-empty">No ATT&amp;CK tactic data for this period.</p>;
  const max = Math.max(...tactics.map((t) => t.count), 1);
  return (
    <ol className="an-chain" aria-label="Attack stages in kill-chain order">
      {tactics.map((t, i) => (
        <li key={t.name} title={`${t.name}: ${t.count} threats`}>
          <span className="an-chain-n">{t.count}</span>
          <span className="an-chain-bar" aria-hidden="true"><i style={{ height: `${Math.max(6, (t.count / max) * 100)}%` }} /></span>
          <span className="an-chain-name"><span className="an-chain-i">{i + 1}</span> {t.name}</span>
        </li>
      ))}
    </ol>
  );
}

function Vulnerabilities({ v }) {
  return (
    <>
      <dl className="an-facts">
        <div><dt>CVEs referenced</dt><dd>{v.distinct}</dd></div>
        <div><dt>Known-exploited</dt><dd className={v.kev ? 'is-warn' : ''}>{v.kev}</dd></div>
        <div><dt>Used in ransomware</dt><dd className={v.kev_ransomware ? 'is-warn' : ''}>{v.kev_ransomware}</dd></div>
        <div><dt>CVSS 9.0 or higher</dt><dd>{v.cvss_critical}</dd></div>
      </dl>
      {!v.kev_available && <p className="an-note">The CISA exploited-vulnerability list could not be reached, so known exploitation is not shown.</p>}
      {v.top.length > 0 && (
        <div className="an-table-wrap">
          <table className="an-table">
            <caption className="sr-only">Highest-priority CVEs referenced in your threats</caption>
            <thead><tr><th scope="col">CVE</th><th scope="col">Product</th><th scope="col">CVSS</th><th scope="col">Threats</th><th scope="col">Open</th><th scope="col">Flags</th></tr></thead>
            <tbody>
              {v.top.map((c) => (
                <tr key={c.cve_id}>
                  <th scope="row" className="an-mono">{c.cve_id}</th>
                  <td>{[c.vendor, c.product].filter(Boolean).join(' ') || 'Not listed'}</td>
                  <td className="an-mono">{c.cvss != null ? c.cvss.toFixed(1) : 'Pending'}</td>
                  <td className="an-mono">{c.briefs}</td>
                  <td className="an-mono">{c.unhandled}</td>
                  <td>
                    {c.kev && <Badge tone="danger" size="sm" icon={Flame}>Exploited</Badge>}{' '}
                    {c.kev_ransomware && <Badge tone="warning" size="sm">Ransomware</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="an-foot-link"><Link to="/vulnerabilities">See every CVE</Link></p>
    </>
  );
}

/**
 * Analytics: what changed, what stands out, and how the team is responding, for the selected window.
 * Everything is computed server-side from matched briefs (services/insights_engine.py). When the
 * account's history does not cover the previous period, comparisons are withheld and say why.
 */
export default function AnalyticsTab({ analytics: a, windowLabel }) {
  const navigate = useNavigate();
  const types = TYPE_PARTS.map(([k, label, color]) => ({ label, value: a.content_types[k] || 0, color }));
  const sevParts = SEV_ORDER.map((s) => ({ label: s, value: a.severity_distribution[s] || 0, color: sevVar(s) }));
  const topAttributed = a.actors.attributed;
  const reasonRows = [
    ...(a.reasons.industry ? [{ label: 'Targets your industry', value: a.reasons.industry }] : []),
    ...(a.reasons.region ? [{ label: 'Active in your region', value: a.reasons.region }] : []),
    ...a.reasons.technologies.map((t) => ({ label: `Names ${t.name}`, value: t.count, color: sevVar('Low') })),
    ...a.reasons.other.map((t) => ({ label: t.name, value: t.count })),
  ];

  return (
    <div className="an" data-testid="analytics-tab">
      <p className="dash-panel-intro">
        {plural(a.total, 'threat')} matched your profile {windowLabel}.
        {a.window.comparable ? ` Changes are measured against the previous ${a.window.days} days.` : ''}
      </p>

      <SectionCard title="What stands out" subtitle="Findings worked out from the numbers below, most important first" icon={TrendingUp}>
        <Insights items={a.insights} total={a.total} />
      </SectionCard>

      <SectionCard title="This period" subtitle={a.window.comparable ? `Change against the previous ${a.window.days} days` : 'Threats by severity'} icon={BarChart3}>
        <PeriodStrip totals={a.totals} window={a.window} />
      </SectionCard>

      <SectionCard title="Activity over time" subtitle="New threats per day by severity" icon={TrendingUp}>
        <DailyBars series={a.daily} height={190} label={`New threats per day, ${a.window.label}`} onSelectDay={(d) => navigate(`/threats?view=all&day=${d}`)} />
      </SectionCard>

      <div className="an-cols">
        <SectionCard title="Severity and type" subtitle="What the threats are" icon={Target}>
          <h3 className="an-sub">By severity</h3>
          <SplitBar parts={sevParts} label="Threats by severity" />
          <h3 className="an-sub an-sub--gap">By type</h3>
          <SplitBar parts={types} label="Threats by type" />
        </SectionCard>
        <SectionCard title="How you are responding" subtitle="Work done on the threats in this period" icon={ShieldCheck}>
          <Response response={a.response} />
        </SectionCard>
      </div>

      <div className="an-cols">
        <SectionCard title="Threat actors" subtitle="Grouped by name and known aliases" icon={Users}>
          <RankedBars
            items={a.actors.top.map((x) => ({ label: x.name, value: x.count, emphasis: x.critical_high }))}
            emphasisLabel="Critical or High"
            empty="No threats in this period name an actor."
          />
          <p className="t-small an-foot">
            {topAttributed > 0
              ? `${topAttributed} of ${a.total} threats (${Math.round((100 * topAttributed) / a.total)}%) name an actor across ${plural(a.actors.distinct, 'group')}. The rest are not attributed.`
              : 'Attribution is often missing early in an incident.'}
          </p>
        </SectionCard>
        <SectionCard title="Techniques" subtitle="Most common methods, counted once per threat" icon={Swords}>
          <RankedBars
            items={a.techniques.map((x) => ({ label: x.name, value: x.count, emphasis: x.critical_high }))}
            emphasisLabel="Critical or High"
            empty="No technique data in this period."
          />
        </SectionCard>
      </div>

      <SectionCard title="Attack stages" subtitle="Threats per MITRE ATT&CK tactic, in the order an attack unfolds" icon={Target}
        footer={<Link to="/attack" className="an-foot-link">Open the ATT&amp;CK view</Link>}>
        <KillChain tactics={a.tactics} />
      </SectionCard>

      <div className="an-cols">
        <SectionCard title="Why threats reach you" subtitle="What they matched on. One threat can match several ways." icon={Target}>
          <RankedBars items={reasonRows} empty="Matches appear here once threats arrive." />
        </SectionCard>
        <SectionCard title="Source quality" subtitle="How well reported the threats are" icon={Link2}>
          <div className="an-response-head">
            <div>
              <span className="an-big">{a.sources.multi_source_pct == null ? '-' : `${a.sources.multi_source_pct}%`}</span>
              <span className="t-small"> backed by two or more sources</span>
            </div>
          </div>
          <ProgressBar value={a.sources.multi_source} total={a.total} label="Threats backed by two or more sources" />
          <h3 className="an-sub an-sub--gap">Most cited outlets</h3>
          <RankedBars items={a.sources.top_domains.map((d) => ({ label: d.domain, value: d.count }))} empty="No sources recorded." />
        </SectionCard>
      </div>

      <SectionCard title="Vulnerability exposure" subtitle="CVEs named in your threats, with CISA exploitation status" icon={Bug}>
        <Vulnerabilities v={a.vulnerabilities} />
      </SectionCard>
    </div>
  );
}
